import { CalendarClock, ChevronRight } from 'lucide-react'
import {
  Button,
  Card,
  DetailHero,
  Drawer,
  DrawerSection,
  MoneyValue,
  MoneyReconciliation,
  StatusBadge,
} from '@shared/design-system/ui'
import type { BalanceSummary } from '@entities/wallet'
import { releaseDatesForDisplay, formatReleaseDate } from '@entities/wallet'
import {
  releaseOverview,
  releaseOverviewAction,
  releaseOverviewAmount,
  releaseOverviewCopy,
  releaseOverviewIcon,
  releaseOverviewLead,
  releaseOverviewMeta,
  releaseOverviewTitle,
  releaseList,
  releaseListMeta,
  releaseListRow,
  releaseListRowLead,
} from './wallet.css'

export interface SelectedRelease {
  date: string
  currency: BalanceSummary['currency']
}

export function UpcomingReleases({
  balances,
  onOpen,
}: {
  balances: BalanceSummary[]
  onOpen: () => void
}) {
  const balance = balances[0]
  if (
    !balance ||
    (balance.nextReleases.length === 0 && Number(balance.awaitingAvailability?.amount ?? 0) === 0)
  )
    return null

  const releaseCount = balance.nextReleases.length
  const entryCount = balance.nextReleases.reduce((total, release) => total + release.count, 0)

  return (
    <Card padding="md" className={releaseOverview}>
      <div className={releaseOverviewLead}>
        <span className={releaseOverviewIcon} aria-hidden="true">
          <CalendarClock size={19} />
        </span>
        <div className={releaseOverviewCopy}>
          <strong className={releaseOverviewTitle}>Agenda de liberações</strong>
          <span>
            Valores com previsão estão distribuídos por data. Valores sem previsão continuam
            pendentes.
          </span>
        </div>
      </div>
      <div className={releaseOverviewAmount}>
        <MoneyValue amount={balance.pending.amount} currency={balance.pending.currency} size="lg" />
        <span className={releaseOverviewMeta}>
          {releaseCount
            ? `${releaseCount} ${releaseCount === 1 ? 'data' : 'datas'} · ${entryCount} ${entryCount === 1 ? 'lançamento' : 'lançamentos'} com previsão`
            : 'Aguardando previsão de liberação'}
        </span>
      </div>
      <Button
        className={releaseOverviewAction}
        variant="secondary"
        size="sm"
        rightIcon={<ChevronRight size={15} />}
        onClick={onOpen}
      >
        Ver todas as datas
      </Button>
    </Card>
  )
}

export function UpcomingReleasesDrawer({
  balances,
  open,
  onClose,
  onSelect,
}: {
  balances: BalanceSummary[]
  open: boolean
  onClose: () => void
  onSelect: (release: SelectedRelease) => void
}) {
  const balance = balances[0]
  const releases = balance
    ? releaseDatesForDisplay(balance)
        .map((release) => ({ ...release, currency: balance.currency }))
        .sort((left, right) => left.date.localeCompare(right.date))
    : []
  const entryCount = releases.reduce((total, release) => total + release.count, 0)

  return (
    <Drawer
      open={open && balance !== undefined}
      onClose={onClose}
      title={balance ? `Agenda de liberações em ${balance.currency}` : 'Agenda de liberações'}
      description={
        balance
          ? `O total abaixo é o mesmo valor exibido em “A liberar” para ${balance.currency}.`
          : undefined
      }
      size="standard"
      variant="financial"
    >
      {balance ? (
        <>
          <DetailHero
            eyebrow="Total a liberar"
            value={
              <MoneyValue
                amount={balance.pending.amount}
                currency={balance.pending.currency}
                size="xl"
              />
            }
            description={`${releases.length} ${releases.length === 1 ? 'data' : 'datas'} · ${entryCount} ${entryCount === 1 ? 'lançamento' : 'lançamentos'}`}
            aside={<StatusBadge label="Previsto" tone="awaiting" />}
          />
          <DrawerSection
            title="Datas previstas"
            description="Abra uma data para conferir os lançamentos e os ajustes que formam o valor."
          >
            <div className={releaseList}>
              {releases.map((release) => (
                <button
                  type="button"
                  className={releaseListRow}
                  key={`${release.currency}:${release.date}`}
                  onClick={() => onSelect({ date: release.date, currency: release.currency })}
                >
                  <span className={releaseListRowLead}>
                    <strong>
                      {formatReleaseDate(release.date, { day: '2-digit', month: 'long' })}
                    </strong>
                    <span className={releaseListMeta}>
                      {release.count} {release.count === 1 ? 'lançamento' : 'lançamentos'}
                    </span>
                  </span>
                  <MoneyValue
                    amount={release.amount.amount}
                    currency={release.amount.currency}
                    size="md"
                  />
                  <ChevronRight size={17} aria-hidden="true" />
                </button>
              ))}
            </div>
            {balance.awaitingAvailability && Number(balance.awaitingAvailability.amount) > 0 ? (
              <div className={releaseListRow}>
                <strong>Aguardando previsão de liberação</strong>
                <MoneyValue {...balance.awaitingAvailability} size="md" />
              </div>
            ) : null}
            <MoneyReconciliation
              total={balance.pending}
              parts={[
                ...balance.nextReleases.map((release) => release.amount),
                ...(balance.awaitingAvailability ? [balance.awaitingAvailability] : []),
              ]}
            />
          </DrawerSection>
        </>
      ) : null}
    </Drawer>
  )
}
